/**
 * Design Verification Items List Page
 *
 * Main page for managing 設計検証項目一覧 sessions.
 * Split layout: session list (left) + detail view (right).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Checklist as VerificationIcon,
  Download as ExportIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@store/index';
import { setChatOpen, selectChatOpen } from '@features/ui/uiSlice';
import { setCurrentMode, setModeData, resetMode } from '@features/chat/chatModeSlice';

import VerificationSessionList from '../components/verification/VerificationSessionList';
import VerificationItemTable from '../components/verification/VerificationItemTable';
import VerificationItemEditor, { ItemFormData } from '../components/verification/VerificationItemEditor';
import VerificationExportDialog from '../components/verification/VerificationExportDialog';
import {
  VerificationSession,
  VerificationUnit,
  VerificationItem,
} from '../types/verification';
import * as verificationService from '../services/verificationService';

/**
 * Serialize a VerificationSession for the chat mode context.
 * Converts to snake_case plain object for backend consumption.
 */
function serializeSessionForChat(session: VerificationSession): Record<string, any> {
  return {
    id: session.id,
    title: session.title,
    model_name: session.modelName || '',
    description: session.description || '',
    units: session.units.map((unit) => ({
      unit_number: unit.unitNumber,
      unit_name: unit.unitName,
      items: unit.items.map((item) => ({
        item_name: item.itemName,
        classifications: item.classifications,
        standard_target: item.standardTarget,
        verification_methods_primary: item.verificationMethodsPrimary,
        verification_methods_secondary: item.verificationMethodsSecondary,
      })),
    })),
  };
}

function buildDrbfmTraceability(session: VerificationSession): Record<string, any> | null {
  const linkedSessionIds = new Set<string>();
  let linkedCount = 0;
  let totalCount = 0;

  for (const unit of session.units) {
    for (const item of unit.items) {
      totalCount++;
      if (item.sourceDrbfmSessionId) {
        linkedSessionIds.add(item.sourceDrbfmSessionId);
        linkedCount++;
      }
    }
  }

  if (linkedSessionIds.size === 0) return null;

  return {
    linked_drbfm_sessions: Array.from(linkedSessionIds),
    linked_items_count: linkedCount,
    total_items_count: totalCount,
  };
}

const VerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const chatOpen = useAppSelector(selectChatOpen);

  // Session list state
  const [sessions, setSessions] = useState<VerificationSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  // Selected session state
  const [selectedSession, setSelectedSession] = useState<VerificationSession | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [itemEditorOpen, setItemEditorOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Form state
  const [newSessionTitle, setNewSessionTitle] = useState('');
  const [newSessionModel, setNewSessionModel] = useState('');
  const [newSessionDesc, setNewSessionDesc] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [unitName, setUnitName] = useState('');
  const [editingUnit, setEditingUnit] = useState<VerificationUnit | null>(null);
  const [editingItem, setEditingItem] = useState<VerificationItem | null>(null);
  const [targetUnitId, setTargetUnitId] = useState<string>('');
  const [deleteTargetId, setDeleteTargetId] = useState<string>('');
  const [deleteType, setDeleteType] = useState<'session' | 'unit' | 'item'>('session');

  // Notifications
  const [snackMessage, setSnackMessage] = useState<string | null>(null);

  // Chat mode state tracking
  const [verificationChatActive, setVerificationChatActive] = useState(false);

  // ==============================================================================
  // Chat Integration
  // ==============================================================================

  const handleToggleChat = useCallback(() => {
    if (!verificationChatActive) {
      // Open chat in verification mode
      dispatch(setCurrentMode('verification'));
      if (selectedSession) {
        dispatch(setModeData({
          verification_session: serializeSessionForChat(selectedSession),
          drbfm_traceability: buildDrbfmTraceability(selectedSession),
        }));
      }
      dispatch(setChatOpen(true));
      setVerificationChatActive(true);
    } else {
      // Close chat and reset mode
      dispatch(setChatOpen(false));
      dispatch(resetMode());
      setVerificationChatActive(false);
    }
  }, [dispatch, selectedSession, verificationChatActive]);

  // Sync session data to chat mode when selected session changes
  useEffect(() => {
    if (verificationChatActive && selectedSession) {
      dispatch(setModeData({
          verification_session: serializeSessionForChat(selectedSession),
          drbfm_traceability: buildDrbfmTraceability(selectedSession),
        }));
    }
  }, [dispatch, selectedSession, verificationChatActive]);

  // Reset mode on unmount
  useEffect(() => {
    return () => {
      if (verificationChatActive) {
        dispatch(resetMode());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationChatActive]);

  // Track external chat close
  useEffect(() => {
    if (verificationChatActive && !chatOpen) {
      dispatch(resetMode());
      setVerificationChatActive(false);
    }
  }, [chatOpen, verificationChatActive, dispatch]);

  // ==============================================================================
  // Data Loading
  // ==============================================================================

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const response = await verificationService.listSessions({ limit: 50 });
      setSessions(response.sessions);
    } catch (err) {
      setSessionsError(err instanceof Error ? err.message : 'Failed to load sessions');
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const loadSessionDetail = useCallback(async (sessionId: string) => {
    setDetailLoading(true);
    try {
      const session = await verificationService.getSession(sessionId);
      setSelectedSession(session);
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to load session');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // ==============================================================================
  // Session Operations
  // ==============================================================================

  const handleSessionSelect = (session: VerificationSession) => {
    loadSessionDetail(session.id);
  };

  const handleCreateSession = async () => {
    if (!newSessionTitle.trim()) return;
    try {
      const created = await verificationService.createSession({
        title: newSessionTitle.trim(),
        modelName: newSessionModel.trim() || undefined,
        description: newSessionDesc.trim() || undefined,
      });
      setCreateDialogOpen(false);
      setNewSessionTitle('');
      setNewSessionModel('');
      setNewSessionDesc('');
      await loadSessions();
      loadSessionDetail(created.id);
      setSnackMessage(t('verification.sessionCreated'));
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to create session');
    }
  };

  const handleDeleteSession = (sessionId: string) => {
    setDeleteTargetId(sessionId);
    setDeleteType('session');
    setDeleteConfirmOpen(true);
  };

  const handleExportSession = async (sessionId: string) => {
    const session = sessions.find((s) => s.id === sessionId) || selectedSession;
    if (!session) return;
    // If the session doesn't have units loaded (list endpoint returns units: []),
    // load full detail first to avoid overwriting the currently loaded session
    if (!session.units || session.units.length === 0) {
      try {
        const fullSession = await verificationService.getSession(sessionId);
        setSelectedSession(fullSession);
      } catch {
        // Fall back to the list session if detail load fails
        setSelectedSession(session);
      }
    } else if (selectedSession?.id !== sessionId) {
      setSelectedSession(session);
    }
    setExportDialogOpen(true);
  };

  // ==============================================================================
  // Unit Operations
  // ==============================================================================

  const handleAddUnit = () => {
    setEditingUnit(null);
    setUnitNumber('');
    setUnitName('');
    setUnitDialogOpen(true);
  };

  const handleEditUnit = (unit: VerificationUnit) => {
    setEditingUnit(unit);
    setUnitNumber(unit.unitNumber);
    setUnitName(unit.unitName);
    setUnitDialogOpen(true);
  };

  const handleSaveUnit = async () => {
    if (!selectedSession || !unitName.trim()) return;
    try {
      if (editingUnit) {
        await verificationService.updateUnit(selectedSession.id, editingUnit.id, {
          unitNumber: unitNumber.trim(),
          unitName: unitName.trim(),
          sortOrder: editingUnit.sortOrder,
        });
      } else {
        await verificationService.addUnit(selectedSession.id, {
          unitNumber: unitNumber.trim(),
          unitName: unitName.trim(),
          sortOrder: selectedSession.units.length,
        });
      }
      setUnitDialogOpen(false);
      loadSessionDetail(selectedSession.id);
      setSnackMessage(editingUnit ? t('success.updated') : t('verification.unitAdded'));
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to save unit');
    }
  };

  const handleDeleteUnit = (unitId: string) => {
    setDeleteTargetId(unitId);
    setDeleteType('unit');
    setDeleteConfirmOpen(true);
  };

  // ==============================================================================
  // Item Operations
  // ==============================================================================

  const handleAddItem = (unitId: string) => {
    setEditingItem(null);
    setTargetUnitId(unitId);
    setItemEditorOpen(true);
  };

  const handleEditItem = (item: VerificationItem) => {
    setEditingItem(item);
    setTargetUnitId(item.unitId);
    setItemEditorOpen(true);
  };

  const handleSaveItem = async (data: ItemFormData) => {
    if (!selectedSession) return;
    try {
      if (editingItem) {
        await verificationService.updateItem(selectedSession.id, editingItem.id, {
          specification: data.specification,
          itemName: data.itemName,
          classifications: data.classifications,
          standardTarget: data.standardTarget,
          verificationMethodsPrimary: data.verificationMethodsPrimary,
          verificationMethodsSecondary: data.verificationMethodsSecondary,
          qualityRecordNo: data.qualityRecordNo,
          qualityRecordTitle: data.qualityRecordTitle,
          reviewJudgment: data.reviewJudgment,
          qualityRecordNoRight: data.qualityRecordNoRight,
          qualityRecordTitleRight: data.qualityRecordTitleRight,
        });
      } else {
        await verificationService.addItem(selectedSession.id, {
          unitId: targetUnitId,
          specification: data.specification,
          itemName: data.itemName,
          classifications: data.classifications,
          standardTarget: data.standardTarget,
          verificationMethodsPrimary: data.verificationMethodsPrimary,
          verificationMethodsSecondary: data.verificationMethodsSecondary,
          qualityRecordNo: data.qualityRecordNo,
          qualityRecordTitle: data.qualityRecordTitle,
          reviewJudgment: data.reviewJudgment,
          qualityRecordNoRight: data.qualityRecordNoRight,
          qualityRecordTitleRight: data.qualityRecordTitleRight,
        });
      }
      setItemEditorOpen(false);
      loadSessionDetail(selectedSession.id);
      setSnackMessage(editingItem ? t('success.updated') : t('verification.itemAdded'));
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to save item');
    }
  };

  const handleDeleteItem = (itemId: string) => {
    setDeleteTargetId(itemId);
    setDeleteType('item');
    setDeleteConfirmOpen(true);
  };

  // ==============================================================================
  // Delete Confirm
  // ==============================================================================

  const handleConfirmDelete = async () => {
    try {
      if (deleteType === 'session') {
        await verificationService.deleteSession(deleteTargetId);
        setSelectedSession(null);
        await loadSessions();
      } else if (deleteType === 'unit') {
        if (!selectedSession) return;
        await verificationService.deleteUnit(selectedSession.id, deleteTargetId);
        loadSessionDetail(selectedSession.id);
      } else if (deleteType === 'item') {
        if (!selectedSession) return;
        await verificationService.deleteItem(selectedSession.id, deleteTargetId);
        loadSessionDetail(selectedSession.id);
      }
      setDeleteConfirmOpen(false);
      setSnackMessage(t('success.deleted'));
    } catch (err) {
      setSnackMessage(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  // ==============================================================================
  // Render
  // ==============================================================================

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, pb: 1 }}>
        <IconButton onClick={() => navigate('/dashboard')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <VerificationIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h5" component="h1">
          {t('verification.title')}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={verificationChatActive ? t('verification.chat.closeTooltip') : t('verification.chat.openTooltip')}>
          <Button
            variant={verificationChatActive ? 'contained' : 'outlined'}
            startIcon={<ChatIcon />}
            onClick={handleToggleChat}
            size="small"
            sx={verificationChatActive ? { bgcolor: '#00897b', '&:hover': { bgcolor: '#00796b' } } : { color: '#00897b', borderColor: '#00897b' }}
          >
            {t('verification.chat.title')}
          </Button>
        </Tooltip>
        {selectedSession && (
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={() => setExportDialogOpen(true)}
            size="small"
          >
            {t('verification.export.excel')}
          </Button>
        )}
      </Box>

      {/* Main content: split layout */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2, px: 2, pb: 2, overflow: 'hidden' }}>
        {/* Left panel: Session list */}
        <Box sx={{ width: 240, flexShrink: 0, overflow: 'auto' }}>
          <VerificationSessionList
            sessions={sessions}
            selectedSessionId={selectedSession?.id || null}
            isLoading={sessionsLoading}
            error={sessionsError}
            onSessionSelect={handleSessionSelect}
            onSessionDelete={handleDeleteSession}
            onSessionExport={handleExportSession}
            onRefresh={loadSessions}
            onCreate={() => setCreateDialogOpen(true)}
          />
        </Box>

        {/* Right panel: Detail view */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
              <CircularProgress />
            </Box>
          )}

          {!detailLoading && !selectedSession && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 8 }}>
              <VerificationIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                {t('verification.selectSession')}
              </Typography>
            </Box>
          )}

          {!detailLoading && selectedSession && (
            <Box>
              {/* Session header */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6">{selectedSession.title}</Typography>
                {selectedSession.modelName && (
                  <Typography variant="body2" color="text.secondary">
                    {t('verification.modelName')}: {selectedSession.modelName}
                  </Typography>
                )}
                {selectedSession.description && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedSession.description}
                  </Typography>
                )}
              </Box>

              {/* Item table */}
              <VerificationItemTable
                session={selectedSession}
                onAddUnit={handleAddUnit}
                onEditUnit={handleEditUnit}
                onDeleteUnit={handleDeleteUnit}
                onAddItem={handleAddItem}
                onEditItem={handleEditItem}
                onDeleteItem={handleDeleteItem}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Create Session Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('verification.sessions.create')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={t('verification.sessionTitle')}
              value={newSessionTitle}
              onChange={(e) => setNewSessionTitle(e.target.value)}
              fullWidth
              size="small"
              required
              autoFocus
            />
            <TextField
              label={t('verification.modelName')}
              value={newSessionModel}
              onChange={(e) => setNewSessionModel(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label={t('verification.sessionDescription')}
              value={newSessionDesc}
              onChange={(e) => setNewSessionDesc(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreateSession} disabled={!newSessionTitle.trim()}>
            {t('verification.sessions.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unit Dialog */}
      <Dialog open={unitDialogOpen} onClose={() => setUnitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUnit ? t('verification.editUnit') : t('verification.addUnit')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={t('verification.unitNumber')}
              value={unitNumber}
              onChange={(e) => setUnitNumber(e.target.value)}
              fullWidth
              size="small"
              placeholder="04"
            />
            <TextField
              label={t('verification.unitNameLabel')}
              value={unitName}
              onChange={(e) => setUnitName(e.target.value)}
              fullWidth
              size="small"
              required
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnitDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveUnit} disabled={!unitName.trim()}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item Editor */}
      <VerificationItemEditor
        open={itemEditorOpen}
        item={editingItem}
        unitId={targetUnitId}
        onSave={handleSaveItem}
        onClose={() => setItemEditorOpen(false)}
      />

      {/* Export Dialog */}
      <VerificationExportDialog
        open={exportDialogOpen}
        session={selectedSession}
        onClose={() => setExportDialogOpen(false)}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('verification.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('verification.deleteConfirmMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!snackMessage}
        autoHideDuration={3000}
        onClose={() => setSnackMessage(null)}
        message={snackMessage}
      />
    </Box>
  );
};

export default VerificationPage;
